import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Quill from 'quill';
import BlotFormatter from '@enzedonline/quill-blot-formatter2';
import ImageDropAndPaste from 'quill2-image-drop-and-paste';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

Quill.register('modules/blotFormatter2', BlotFormatter);
Quill.register('modules/imageDropAndPaste', ImageDropAndPaste);

const readImageAsDataUrl = (file: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

const BlockEmbed = Quill.import('blots/block/embed') as any;
const QuillDelta = Quill.import('delta') as any;

export const PAGE_BREAK_CLASS = 'ql-page-break';

class PageBreakBlot extends BlockEmbed {
  static blotName = 'pageBreak';

  static tagName = 'div';

  static className = PAGE_BREAK_CLASS;

  static create(): HTMLElement {
    const node = super.create() as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('style', 'page-break-after: always;');
    return node;
  }

  static value(): boolean {
    return true;
  }
}

Quill.register(PageBreakBlot as any, true);

const quillIcons = Quill.import('ui/icons') as Record<string, string>;
quillIcons['pageBreak'] = `<svg viewBox="0 0 18 18">
    <rect class="ql-stroke" height="4" width="10" x="4" y="2"></rect>
    <line class="ql-stroke" x1="2" y1="9" x2="4" y2="9"></line>
    <line class="ql-stroke" x1="7" y1="9" x2="11" y2="9"></line>
    <line class="ql-stroke" x1="14" y1="9" x2="16" y2="9"></line>
    <rect class="ql-stroke" height="4" width="10" x="4" y="12"></rect>
  </svg>`;

@Component({
    selector: 'app-richtext-editor',
    templateUrl: './richtext-editor.component.html',
    styleUrls: ['./richtext-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RichtextEditorComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RichtextEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  editor: any;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private initialContent = '';
  contentChangeSubject = new Subject<string>();

  @Input() minHeight = '300px';
  @Output() contentChanged = new EventEmitter<string>();

  @Input() set content(value: string) {
    if (this.initialContent !== value) {
      this.initialContent = value;
      if (this.editor && this.editor.root.innerHTML !== value) {
        const range = this.editor.getSelection();
        this.editor.root.innerHTML = value;
        if (range) {
          this.editor.setSelection(range.index, range.length);
        }
      }
    }
  }

  constructor(private elementRef: ElementRef, private renderer: Renderer2) {
    this.contentChangeSubject.pipe(debounceTime(300)).subscribe((content) => {
      this.contentChanged.emit(content);
      this.onChange(content); // Notify Angular Form of content change
    });
  }

  ngAfterViewInit() {
    const container = this.elementRef.nativeElement.querySelector('#editor-container');
    if (container) {
      this.editor = new Quill(container, {
        theme: 'snow',
        readOnly: false,
        modules: {
          toolbar: {
            container: [
              [{
                header: [1, 2, 3, 4, 5, 6, false],
              }],
              ['bold', 'italic', 'underline', 'strike'],
              [{
                color: [],
              }, {
                background: [],
              }],
              [{
                list: 'ordered',
              }, {
                list: 'bullet',
              }],
              [{
                align: [],
              }],
              ['image', 'pageBreak'],
            ],
            handlers: {
              pageBreak: () => this.insertPageBreak(),
            },
          },
          blotFormatter2: {
            align: {
              allowAligning: false,
            },
            resize: {
              allowResizing: true,
            },
            delete: {
              allowKeyboardDelete: true,
            },
            image: {
              allowAltTitleEdit: false,
              allowCompressor: false,
              linkOptions: {
                allowLinkEdit: false,
              },
            },
          },
          imageDropAndPaste: {
            upload: readImageAsDataUrl,
          },
        },
      });
      this.editor.clipboard.addMatcher(
        `div.${PAGE_BREAK_CLASS}`,
        () => new QuillDelta().insert({
          pageBreak: true,
        }),
      );
      this.renderer.setStyle(this.editor.root, 'min-height', this.minHeight);
      this.renderer.setStyle(this.editor.root, 'height', 'fit-content');
      this.renderer.setStyle(this.editor.root, 'overflow-y', 'visible');
      this.editor.root.innerHTML = this.initialContent;

      const pageBreakButton = this.elementRef.nativeElement.querySelector('button.ql-pageBreak');
      if (pageBreakButton) {
        this.renderer.setAttribute(pageBreakButton, 'title', 'Insert page break');
      }

      this.editor.on('text-change', () => {
        const content = this.editor.root.innerHTML;
        if (content === '<p><br></p>' || content.trim() === '<p><br></p>') {
          this.contentChangeSubject.next(''); // Send empty string
        } else {
          this.contentChangeSubject.next(content);
        }
      });
    } else {
      console.error('Editor container not found');
    }
  }

  insertPageBreak(): void {
    if (!this.editor) {
      return;
    }
    const range = this.editor.getSelection(true);
    const index = range ? range.index : this.editor.getLength();
    this.editor.insertEmbed(index, 'pageBreak', true, 'user');
    this.editor.setSelection(index + 1, 0, 'silent');
  }

  ngOnDestroy() {
    if (this.editor) {
      this.editor.disable();
    }
    this.contentChangeSubject.unsubscribe();
  }

  // ControlValueAccessor Implementation
  writeValue(value: string): void {
    // if (this.editor && this.editor.root.innerHTML !== value) {
    //   this.editor.root.innerHTML = value || '';
    // }
    /* if (this.editor) {
      const currentRange = this.editor.getSelection(); // Get current cursor position
      this.editor.clipboard.dangerouslyPasteHTML(value || ''); // Update content safely
      if (currentRange) {
        this.editor.setSelection(currentRange.index, currentRange.length); // Restore cursor position
      }
    } else {
      this.initialContent = value || ''; // Fallback for uninitialized editor
    } */
    if (this.editor) {
      if (this.editor.root.innerHTML !== value) {
        const { scrollTop } = this.editor.root;
        this.editor.clipboard.dangerouslyPasteHTML(value || '');
        this.editor.root.scrollTop = scrollTop;
      }
    } else {
      this.initialContent = value || '';
    }
    // if (this.editor) {
    //   this.editor.root.innerHTML = value || '';
    // } else {
    //   this.initialContent = value || '';
    // }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.editor) {
      this.editor.enable(!isDisabled);
    }
  }
}
